import {
  OreButton,
  OreList,
  OreListItem,
  OrePill,
  OreStack,
  OreTableCell,
  OreTableRow,
  OreText,
  SvgDownload,
  SvgRepeat,
  SvgWarning,
  SvgAlert
} from '@edenredespana/oreneta'
import { Trans, useTranslation } from 'react-i18next'
import { ExcelLayout } from '@/Store/ui/templates/ExcelLayout'
import { useExcelUpload } from '@/Store/ui/context/ExcelUploadContext'
import { SimpleTable } from '@/Shared/ui/Table'
import { storesTranslation } from '@/Store/ui/storesTranslation'
import { CancelModal } from '@/Store/ui/molecules'
import { Steps } from '@/Store/ui/atoms'

export const ExcelValidation = (): JSX.Element => {
  const { t } = useTranslation()
  const {
    uploadResponse,
    validateForm: { formState, handleSubmit },
    confirm,
    setDisplayCancelModal,
    downloadErrorsFile,
    setStep,
  } = useExcelUpload()

  const getPill = (errorLevel: string) => {
    if (errorLevel === 'WARNING') {
      return <OrePill
        tone="warning-600"
        icon={<SvgWarning />}>
        {t(storesTranslation.excelTpvValidation.pillWarning)}
      </OrePill>
    } else {
      return <OrePill
        tone="error-600"
        icon={<SvgAlert />}>
        {t(storesTranslation.excelTpvValidation.pillError)}
      </OrePill>
    }
  }

  return (
    <ExcelLayout
      onCancel={() => setDisplayCancelModal(true)}
      onContinue={handleSubmit(confirm)}
      continueDisabled={!formState.isValid}>
      <OreStack space="large" placeContent="stretch">
        <SimpleTable
          title={t(storesTranslation.excelTpvValidation.errorsTableTitle)}
          count={<Trans>{uploadResponse?.rowErrors?.length === 1 ?
            t(storesTranslation.excelTpvValidation.errorsTableCount, { ERROR_COUNT: uploadResponse?.rowErrors?.length.toString() }) :
            t(storesTranslation.excelTpvValidation.errorsTableCountPlural, { ERROR_COUNT: uploadResponse?.rowErrors?.length.toString() })}
          </Trans> as unknown as string}
          tableHeaders={[
            {
              label: t(storesTranslation.excelTpvValidation.row),
              key: 'rowNumber',
              size: 'quarter',
            },
            {
              label: t(storesTranslation.excelTpvValidation.errorLevel),
              key: 'errorLevel',
              size: 'quarter',
            },
            {
              label: t(storesTranslation.excelTpvValidation.errorMessage),
              key: 'errorMessage',
              size: 'auto',
            },
          ]}
          rows={[
            ...uploadResponse?.rowErrors ?? []
          ]}
          renderRow={(row, index) => {
            return (
              <OreTableRow key={index}>
                <OreTableCell><OreText>{row.rowNumber}</OreText></OreTableCell>
                <OreTableCell>{getPill(row.errorLevel)}</OreTableCell>
                <OreTableCell>
                  {row.errorMessage}
                </OreTableCell>
              </OreTableRow>
            )
          }}
        />
        <OreStack direction="column" space="large">
          <OreStack direction="column" space="medium">
            <OreText>{t(storesTranslation.excelTpvValidation.errorLegendWarning)}</OreText>
            <OreList bullet>
              <OreListItem>
                <OreText>{t(storesTranslation.excelTpvValidation.errorLegendListItem1)}</OreText>
              </OreListItem>
              <OreListItem>
                <OreText>{t(storesTranslation.excelTpvValidation.errorLegendListItem2)}</OreText>
              </OreListItem>
            </OreList>
          </OreStack>
          <OreStack space="medium" direction="row">
            <OreButton
              onClick={downloadErrorsFile}
              icon={<SvgDownload />}
              category='secondary'>{t(storesTranslation.excelTpvValidation.downloadButton)}</OreButton>
            <OreButton
              onClick={() => setStep(Steps.Upload)}
              icon={<SvgRepeat />}
              category='secondary'>{t(storesTranslation.excelTpvValidation.changeFileButton)}</OreButton>
          </OreStack>
        </OreStack>
      </OreStack>
      <CancelModal />
    </ExcelLayout>
  )
}
